/**
 * @fileoverview A notice (a notice rule in the Grow settings' rules list): a
 * beta tester's feedback at two weeks, a word to subscribers, anything worth
 * saying to the right gardeners at the right time. Which notice shows, and
 * whether it has been put away, is the server's (todo.reads.notice; the to-do
 * rebuild, batch 5): one at a time, in the panel's order; before a postcode,
 * none.
 *
 * Urgent ones (a notice that cannot be put away) sit at the very top, above
 * the picture, in the alert's cold colour. The rest sit under the weather,
 * quieter, in the place of the weather's advice (the server places only one
 * of them there), with a way to put them away (for good, or for today, as the
 * rule says).
 *
 * A button follows the notice's link (a feedback form): a page in the app
 * through the router, a web page in a new tab, an email address or a phone
 * number as it is. A link that is not safe, or goes nowhere, gets no button
 * (safeLink).
 *
 * Where: Today: an urgent notice at the very top, a quiet one under the weather.
 * API: none of its own; seen and put away go through the tips layer
 * (useTips: POST /todo/marks).
 * Words (en/es): the notice's own (the server's, {when} already "today"),
 * tips.close, tips.notNow, tips.gotIt.
 *
 * @module TodayNotice
 */

import React from 'react';
import { BellRing, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { LinkButton } from '@/shared/components/LinkButton';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { readLink } from '@/shared/lib/safeLink';
import { useTips } from '@/features/tips/useTips';
import { useSeen } from '@/features/tips/TipViews';
import type { Advice } from '@/features/tips/types';

/** One notice, as the server placed it. */
interface TodayNoticeProps {
  /** The notice: its words, its link, and how it is put away. */
  notice: Advice;
}

/**
 * A notice, urgent at the top or quiet under the weather; said seen while it is drawn.
 *
 * @param props - TodayNoticeProps.
 * @returns The notice.
 */
export const TodayNotice: React.FC<TodayNoticeProps> = ({ notice }) => {
  const { reactTranslate: t } = useTranslation();
  const tips = useTips();
  useSeen(notice);
  const { title, body } = tips.words(notice);
  // The button only with somewhere safe to go: the panel's text is typed by hand.
  const link = readLink(notice.button?.url);
  const button = link ? (notice.button?.text ?? '').trim() : '';
  /** Puts it away, as its rule says (for good, or for today). */
  const putAway = (): void => tips.mark(notice, 'put_away');

  if (notice.urgent) {
    return (
      <section role="alert" className="flex items-start gap-3 rounded-lg border-l-4 border-verdict-cold bg-verdict-cold-tint px-4 py-3 text-foreground">
        <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-verdict-cold" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          {title && <p className="text-base font-bold leading-snug">{title}</p>}
          {body && <p className="text-sm">{body}</p>}
          {button && link && <LinkButton size="sm" link={link}>{button}</LinkButton>}
        </div>
      </section>
    );
  }

  const canPutAway = notice.putAway !== 'never';
  return (
    <section className="relative rounded-lg border bg-card p-4 pr-10">
      {canPutAway && (
        <button
          type="button"
          onClick={putAway}
          aria-label={t('tips.close')}
          className="absolute right-1 top-1 rounded-sm p-2.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      {title && <h2 className="font-semibold leading-snug font-body">{title}</h2>}
      {body && <p className="mt-1 text-sm text-muted-foreground">{body}</p>}
      {(button || canPutAway) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {button && link && <LinkButton size="sm" link={link}>{button}</LinkButton>}
          {canPutAway && (
            <Button size="sm" variant="outline" onClick={putAway}>
              {button ? t('tips.notNow') : t('tips.gotIt')}
            </Button>
          )}
        </div>
      )}
    </section>
  );
};
